import { 
  Eye, 
  Edit, 
  Trash2, 
  Send, 
  Users,
  BarChart3,
  Loader2
} from 'lucide-react';
import { formatDateTime } from '../../../../utils/helperFunction';

const NewsletterTable = ({ 
  campaigns, 
  loading, 
  sendingCampaignId,
  onEdit, 
  onDelete,
  onSend, 
  getStatusIcon, 
  getAudienceLabel 
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatDateTime(dateString);
  };
  console.log("campaigns in table", campaigns);
  const getStatusBadge = (status) => {
    const badges = {
      sent: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
    };
    
    return badges[status] || badges.draft;
  };

  // Calculate audience count from selectedUsers array in API response
  const getAudienceCount = (campaign) => {
    // Use selectedUsers array length from getAllCampaigns API response
    if (campaign?.selectedUsers && Array.isArray(campaign.selectedUsers)) {
      return campaign.selectedUsers.length;
    }
    
    // Fallback to saved audienceCount if selectedUsers not available
    if (campaign?.audienceCount) {
      return campaign.audienceCount;
    }
    
    return 0;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-4 bg-bg-40 rounded w-1/4"></div>
              <div className="h-4 bg-bg-40 rounded w-1/4"></div>
              <div className="h-4 bg-bg-40 rounded w-1/6"></div>
              <div className="h-4 bg-bg-40 rounded w-1/6"></div>
              <div className="h-4 bg-bg-40 rounded w-1/6"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="p-12 text-center">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-bg-40 rounded-full flex items-center justify-center mb-4">
            <Send size={24} className="text-fg-60" />
          </div>
          <h3 className="text-lg font-medium text-fg-50 mb-2">No campaigns found</h3>
          <p className="text-fg-60 mb-4">Get started by creating your first email campaign</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-bd-50">
        <thead className="bg-bg-40">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
              Campaign
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
              Audience
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
              Performance
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-fg-60 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-bg-60 divide-y divide-bd-50">
          {campaigns.map((campaign, index) => {
            // Handle both MongoDB _id and regular id
            const campaignId = campaign._id || campaign.id || index;
            return (
            <tr key={campaignId} className="hover:bg-bg-50/30 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-fg-50">{campaign?.title || 'Untitled'}</span>
                    {campaign?.type && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        campaign.type === 'newsletter' 
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' 
                          : campaign.type === 'announcement'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                      }`}>
                        {campaign.type === 'newsletter' 
                          ? '📰 Newsletter' 
                          : campaign.type === 'announcement'
                          ? '📢 Announcement'
                          : '🔔 Notification'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-fg-60">{campaign?.subject || 'No subject'}</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  {getStatusIcon(campaign?.status || 'draft')}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(campaign?.status || 'draft')}`}>
                    {(campaign?.status || 'draft').charAt(0).toUpperCase() + (campaign?.status || 'draft').slice(1)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-fg-60" />
                  <div>
                    <div className="text-sm text-fg-50">{getAudienceLabel(campaign?.audience || 'all_users')}</div>
                    <div className="text-xs text-fg-60">{getAudienceCount(campaign)} recipients</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {campaign?.status === 'sent' && campaign?.openRate !== undefined ? (
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-sm font-medium text-fg-50">{campaign.openRate}%</div>
                      <div className="text-xs text-fg-60">Opens</div>
                    </div>
                    {campaign?.clickRate !== undefined && (
                      <div className="text-center">
                        <div className="text-sm font-medium text-fg-50">{campaign.clickRate}%</div>
                        <div className="text-xs text-fg-60">Clicks</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-fg-60">-</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-fg-50">
                  {campaign?.status === 'sent' && campaign?.sentAt
                    ? formatDate(campaign.sentAt)
                    : campaign?.status === 'scheduled' && campaign?.scheduledFor
                    ? formatDate(campaign.scheduledFor)
                    : formatDate(campaign?.createdAt)
                  }
                </div>
                <div className="text-xs text-fg-60">
                  {campaign?.status === 'sent' 
                    ? 'Sent' 
                    : campaign?.status === 'scheduled' 
                    ? 'Scheduled' 
                    : 'Created'
                  }
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  {/* Preview button - Always disabled */}
                  <button
                    disabled
                    className="p-1.5 text-fg-60 opacity-40 cursor-not-allowed rounded transition-colors"
                    title="Preview (Coming Soon)"
                  >
                    <Eye size={16} />
                  </button>
                  
                  {/* Edit button - Disabled when status is sent */}
                  <button
                    onClick={() => campaign.status !== 'sent' && onEdit(campaign)}
                    disabled={campaign.status === 'sent'}
                    className={`p-1.5 rounded transition-colors ${
                      campaign.status === 'sent'
                        ? 'text-fg-60 opacity-40 cursor-not-allowed'
                        : 'text-fg-60 hover:text-fg-50 hover:bg-bg-40'
                    }`}
                    title={campaign.status === 'sent' ? 'Cannot edit sent campaigns' : 'Edit'}
                  >
                    <Edit size={16} />
                  </button>
                  
                  {/* Analytics button for sent campaigns - Always disabled */}
                  {campaign.status === 'sent' && (
                    <button
                      disabled
                      className="p-1.5 text-fg-60 opacity-40 cursor-not-allowed rounded transition-colors"
                      title="View Analytics (Coming Soon)"
                    >
                      <BarChart3 size={16} />
                    </button>
                  )}
                  
                  {/* Send now button for drafts */}
                  {campaign.status === 'draft' && (
                    <button
                      onClick={() => onSend(campaign)}
                      disabled={sendingCampaignId === (campaign._id || campaign.id)}
                      className={`p-1.5 rounded transition-colors ${
                        sendingCampaignId === (campaign._id || campaign.id)
                          ? 'text-blue-400 bg-blue-50 dark:bg-blue-900/20 cursor-not-allowed'
                          : 'text-blue-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      }`}
                      title={sendingCampaignId === (campaign._id || campaign.id) ? "Sending..." : "Send Now"}
                    >
                      {sendingCampaignId === (campaign._id || campaign.id) ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  )}
                  
                  {/* Delete button */}
                  <button
                    onClick={() => onDelete(campaign)}
                    className="p-1.5 text-red-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default NewsletterTable;